// src/modules/calendar/utils/eventDescription.js
// Genera la descripción de un evento de calendario de forma dinámica a partir
// de los datos que ya tenemos (título, fecha/hora, instructor, enlace de clase).
// El formato se controla con una plantilla editable por el usuario que usa
// variables tipo {titulo}, {hora}, {fecha}, {instructor}, {instituto}, {enlace}, {id}, {pass}.

const MONTHS = [
    'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
    'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];

// Nombre del instituto que aparece en la descripción.
export const INSTITUTO = 'HINGENIA';

// Variables disponibles para la plantilla (se muestran como ayuda en el editor).
export const TEMPLATE_VARS = ['titulo', 'hora', 'fecha', 'instructor', 'instituto', 'enlace', 'id', 'pass'];

// Plantilla por defecto. Las líneas cuyas variables queden TODAS vacías se omiten
// (p.ej. el bloque de Zoom cuando el evento no tiene enlace).
export const DEFAULT_TEMPLATE = `{titulo}

Hora: {hora}, {fecha}

Instructor: 🧑‍🏫 {instructor}
Instituto: {instituto} 🏫

Enlace clase en vivo: {enlace}
ID: {id}   Contraseña: {pass}`;

// Formato 12 horas: "08:00 p.m."
const to12h = (d) => {
    let h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? 'p.m.' : 'a.m.';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Calcula el valor de cada variable a partir del evento/curso/instructor.
// `classInfo` (opcional) trae la posición del evento en su secuencia de curso
// (p.ej. { num: 1, total: 6, type: 'CL' }), calculada en utils/eventEnumeration.js,
// y se usa para anteponer el número de clase/masterclass al título.
const buildTokens = (event, course, instructor, classInfo) => {
    const start = event.start instanceof Date ? event.start : new Date(event.start);
    const isClase = event.type === 'clase' || event.resource?.type === 'clase';
    const baseTitle = course?.name || event.title || 'Clase';

    let titulo = event.title || course?.name || 'Clase';
    if (classInfo && classInfo.num && !classInfo.cancelled) {
        const label = classInfo.type === 'MC' ? 'Masterclass' : 'Clase';
        titulo = `${label} ${classInfo.num}${classInfo.total ? ` de ${classInfo.total}` : ''}: ${baseTitle}`;
    }

    return {
        titulo,
        hora: to12h(start),
        fecha: `${start.getDate()} DE ${MONTHS[start.getMonth()]} DE ${start.getFullYear()}`,
        instructor: instructor?.name || event.instructorName || '',
        instituto: INSTITUTO,
        enlace: isClase
            ? (event.claseLiveUrl || course?.claseVariables?.claseLiveUrl || '')
            : (event.mcLiveUrl || course?.defaultVariables?.zoomLink || ''),
        id: isClase
            ? (event.claseMeetingId || course?.claseVariables?.claseMeetingId || '')
            : (event.mcMeetingId || course?.defaultVariables?.meetingId || ''),
        pass: isClase
            ? (event.clasePassword || course?.claseVariables?.clasePassword || '')
            : (event.mcPassword || course?.defaultVariables?.password || ''),
    };
};

/**
 * Construye el texto de descripción de un evento aplicando una plantilla.
 * @param {object} event       Evento del calendario
 * @param {object} [course]    Curso asociado
 * @param {object} [instructor] Instructor asociado
 * @param {string} [template]  Plantilla personalizada (usa DEFAULT_TEMPLATE si está vacía)
 * @param {object} [classInfo] Posición del evento en su curso ({ num, total, type }), ver utils/eventEnumeration.js
 * @returns {string}
 */
export function buildEventDescription(event, course, instructor, template, classInfo) {
    if (!event) return '';

    const tokens = buildTokens(event, course, instructor, classInfo);
    const tpl = (template && template.trim()) ? template : DEFAULT_TEMPLATE;

    return tpl
        .split('\n')
        .map((line) => {
            const used = [...line.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);
            // Si la línea usa variables y TODAS están vacías, se omite.
            if (used.length > 0 && used.every((k) => !tokens[k])) return null;
            return line.replace(/\{(\w+)\}/g, (_, k) => (tokens[k] != null ? String(tokens[k]) : ''));
        })
        .filter((l) => l !== null)
        .join('\n');
}
